// Web USB API wrapper for non-MIDI controllers.

export type UsbDataCallback = (data: DataView) => void

export class UsbManager {
  private device: USBDevice | null = null
  private listeners: UsbDataCallback[] = []
  private polling = false

  async requestDevice(filters?: USBDeviceFilter[]): Promise<USBDevice | null> {
    if (!navigator.usb) {
      console.warn("Web USB API not supported in this browser")
      return null
    }

    try {
      this.device = await navigator.usb.requestDevice({
        filters: filters ?? [],
      })
      return this.device
    } catch (err) {
      console.error("USB device request failed:", err)
      return null
    }
  }

  async connect(
    configIndex = 1,
    interfaceIndex = 0,
  ): Promise<boolean> {
    if (!this.device) return false

    try {
      await this.device.open()
      await this.device.selectConfiguration(configIndex)
      await this.device.claimInterface(interfaceIndex)
      return true
    } catch (err) {
      console.error("USB connection failed:", err)
      return false
    }
  }

  async startPolling(endpointNumber: number, packetSize = 64): Promise<void> {
    if (!this.device || this.polling) return
    this.polling = true

    while (this.polling && this.device) {
      try {
        const result = await this.device.transferIn(endpointNumber, packetSize)
        if (result.data && result.data.byteLength > 0) {
          for (const listener of this.listeners) {
            listener(result.data)
          }
        }
      } catch {
        if (this.polling) {
          console.warn("USB read error, stopping poll")
          this.polling = false
        }
      }
    }
  }

  stopPolling(): void {
    this.polling = false
  }

  onData(callback: UsbDataCallback): () => void {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter((l) => l !== callback)
    }
  }

  async disconnect(): Promise<void> {
    this.stopPolling()
    if (this.device) {
      try {
        await this.device.close()
      } catch {
        // Already closed
      }
      this.device = null
    }
  }

  get connected(): boolean {
    return this.device?.opened ?? false
  }

  get connectedDevice(): USBDevice | null {
    return this.device
  }
}
